import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useLocation } from "react-router"
import { setFigure, setFigureCommand } from "../logic/chartFigure"
import { setPlotType, setViewType } from "../logic/chartPlot"
import {
  setSymbolAndLegs,
  setSubSymbol,
  removeSubSymbol,
} from "../logic/chartSymbolAndLeg"
import {
  FIGURE_COMMAND,
  PLOTTYPE,
  TOOLS,
  ADDON_CHART,
  SUB_CHART,
} from "../logic/chartConfig"
import {
  changeFigureCommand,
  changeSubSymbolNode,
  figureAllClear,
  changeAshiCount,
  changeChartDate,
  changeSymbol,
  changePlotType,
  changeTerm,
  changeAddonChart,
  changeSubChart,
  pickTarget,
  changeAddonSelected,
  changeSubSelected,
  changeRelativeChartFlag,
} from "redux/ducks/Chart"
import _ from "lodash"
import useCprovder from "../logic/useCprovider"
import useChartResize from "../logic/useChartResize"
import chartColors from "../logic/chartColor"
import { setAddonChart, removeAddonChart } from "../logic/chartAddon"
import { setSubChart, removeSubChart } from "../logic/chartSub"
import { UserStorage } from "utils/userStorage"
import {
  getAshiCountandSpan,
  convertDate,
  resetUiSpan,
} from "components/atoms/chart/logic/chartSpan"
import { parseQueryString } from "utils/util"
import { easyDecrypt } from "utils/encrypt"
import APIs from "redux/apis"
import { pageLoadRegist } from "redux/ducks/PageLoad"
import { useEffectFirstSkip } from "components/actions/customHook/useEffectFirstSkip"

interface Window {
  Logic: any
  Trading: any
}

declare var window: Window

interface props {
  canvasId: string
  resizeFlag: boolean
}

class subBrandId {
  subBrandId: Array<any>
  constructor() {
    this.subBrandId = []
  }
  setSubBrandId = (data: Array<number>) => {
    this.subBrandId = data
  }
  getSubBrandId = () => {
    return this.subBrandId
  }
}

const TechChart = React.memo((props: props) => {
  const { canvasId, resizeFlag } = props
  const dispatch = useDispatch()
  const location = useLocation()
  const param = useMemo(() => {
    if (!location.search) return
    return parseQueryString(`?${easyDecrypt(location.search.slice(1))}`)
  }, [location.search])
  // Selector
  const allPropertiesSelector = (state: any) => state.chart[canvasId]
  const plotTypeSelector = (state: any) => state.chart[canvasId].plotType
  const providersSelector = (state: any) => state.chart[canvasId].providers
  const symbolSelector = (state: any) => state.chart[canvasId].symbol
  const subSymbolSelector = (state: any) => state.chart[canvasId].subSymbol
  const subSymbolNodeSelector = (state: any) =>
    state.chart[canvasId].subSymbolNode
  const termSelector = (state: any) => state.chart[canvasId].term
  const figureTypeSelector = (state: any) => state.chart[canvasId].figureType
  const figureOptionSelector = (state: any) =>
    state.chart[canvasId].figureOptions
  const figureCommandSelector = (state: any) =>
    state.chart[canvasId].figureCommand
  const colorSettingSelector = (state: any) => state.chart.colorSetting
  const chartSettingSelector = (state: any) => state.chart.chartSetting
  const addonChartSelector = (state: any) => state.chart[canvasId].addonChart
  const addonChartOptionsSelector = (state: any) =>
    state.chart[canvasId].addonChartOptions
  const addonSelectedSelector = (state: any) =>
    state.chart[canvasId].addonSelected
  const subChartSelector = (state: any) => state.chart[canvasId].subChart
  const subChartOptionsSelector = (state: any) =>
    state.chart[canvasId].subChartOptions
  const subSelectedSelector = (state: any) => state.chart[canvasId].subSelected
  const spredSelector = (state: any) => state.chart[canvasId].spred
  const renderCountsSelector = (state: any) => state.chart[canvasId].renderCount
  const toolsSelector = (state: any) => state.chart[canvasId].tools
  const relativeChartFlagSelector = (state: any) =>
    state.chart[canvasId].relativeChartFlag

  // redux state
  const allProperties = useSelector(allPropertiesSelector)
  const plotType = useSelector(plotTypeSelector)
  const term = useSelector(termSelector)
  const figureType = useSelector(figureTypeSelector)
  const figureOptions = useSelector(figureOptionSelector)
  const figureCommand = useSelector(figureCommandSelector)
  const providers = useSelector(providersSelector)

  const symbol = useSelector(symbolSelector)
  const subSymbol = useSelector(subSymbolSelector)
  const subSymbolNode = useSelector(subSymbolNodeSelector)
  const spred = useSelector(spredSelector)
  const addonChart = useSelector(addonChartSelector)
  const addonChartOptions = useSelector(addonChartOptionsSelector)
  const addonSelected = useSelector(addonSelectedSelector)
  const subChart = useSelector(subChartSelector)
  const subChartOptions = useSelector(subChartOptionsSelector)
  const subSelected = useSelector(subSelectedSelector)
  const colorSetting = useSelector(colorSettingSelector)
  const chartSetting = useSelector(chartSettingSelector)
  const renderCount = useSelector(renderCountsSelector)
  const tools = useSelector(toolsSelector)
  const relativeChartFlag = useSelector(relativeChartFlagSelector)

  const [prevAddonSelected, setPrevAddonSelected] = useState(addonSelected)
  const [prevSubSelected, setPrevSubSelected] = useState(subSelected)

  const [_provider, _controller, _procs] = useMemo(() => {
    return providers
  }, [providers])

  const [compChartColor, setCompChartColor] = useState({
    spread1: { r: 0, g: 255, b: 255, a: 255 },
    spread2: { r: 0, g: 255, b: 0, a: 255 },
    sub1: { r: 255, g: 150, b: 0, a: 255 },
    sub2: { r: 60, g: 120, b: 255, a: 255 },
  })

  // ChartProviderを作る関数。ReduxのProvidersにF#のapiを保存する。
  const [createCProvider, cursor] = useCprovder({
    canvasId: canvasId,
  })
  // レスポンシブ対応。windowイベントに追加する関数。
  const { resize, removeResize, size } = useChartResize({
    canvasId: canvasId,
    parentElement: `chart-body-${canvasId}`,
    heightFlag: true,
    resizeTrigger: resizeFlag,
  })

  const { width, height, scaleWidth, scaleHeight } = useMemo(() => {
    return size
  }, [size])

  const _setCompChartColor = useMemo<Function>(() => {
    return chartColors.setCompChartColor(colorSetting)
  }, [colorSetting])

  // F#用 データをクラスでバインドしておかないとF#側から参照できない為、使用。
  const _subBrandId = useMemo(() => {
    return new subBrandId()
  }, [])
  useMemo(() => {
    _subBrandId.setSubBrandId([
      subSymbolNode[0] ? subSymbolNode[0].Id : null,
      subSymbolNode[1] ? subSymbolNode[1].Id : null,
    ])
  }, [subSymbolNode])

  // F#用 スプレッドのカラー設定用関数
  const _mdnIndColor = useMemo(() => {
    return chartColors.mdnIndColor(_subBrandId.getSubBrandId(), compChartColor)
  }, [subSymbolNode, compChartColor])
  // F#用 対比銘柄のカラー設定用関数
  const _nodePlot = useMemo(() => {
    return chartColors.nodePlot(_subBrandId, compChartColor)
  }, [subSymbolNode, compChartColor])

  const colorSettings = () => {
    // スプレッドの色
    _controller.Chart.SetMDNColorF(_mdnIndColor)
    // 対比銘柄の色
    const matchValue1 = _controller.Chart.get_Chart().get_View()
    if (matchValue1 instanceof window.Trading.Chart.TSView) {
      matchValue1.SetSubDataToPlotF(_nodePlot)
    }
  }

  const applyOption = () => {
    setSymbolAndLegs(term.key, symbol, _provider, _controller)
    if (!_.isEmpty(subSymbolNode))
      _.forEach(subSymbolNode, items => removeSubSymbol(items, _controller))
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
    colorSettings()
    _setSubSymbol()
    _setPlotType()
    if (!relativeChartFlag) {
      _setAddonChart()
      _setSubChart()
    }
    _controller.Chart.set_BarNumMargin([renderCount * 0.5, 5])
  }

  const _setSubSymbol = () => {
    if (plotType.key === "PandF") return
    if (!_.isEmpty(subSymbol)) {
      let _symbolNode: Array<object | null> = []
      _.forEach(subSymbol, (symbol, key: number) => {
        if (!symbol.visible) return
        _symbolNode.push(
          setSubSymbol(
            term.key,
            plotType.key,
            key + 1,
            symbol,
            renderCount,
            _provider,
            _controller
          )
        )
      })
      dispatch(changeSubSymbolNode(canvasId, _symbolNode))
    }
  }

  const _setSubChart = () => {
    _.forEach(subChart[term.id], items => {
      let keys: Array<string> = []
      _.forEach(subChartOptions[term.id][items.key], options => {
        keys.push(options.key)
      })
      setSubChart(chartSetting, items.key, _controller, term.key, keys)
    })
  }

  const _setAddonChart = () => {
    _.forEach(addonChart[term.id], items => {
      let keys: Array<string> = []
      _.forEach(addonChartOptions[term.id][items.key], options => {
        keys.push(options.key)
      })
      setAddonChart(chartSetting, items.key, _controller, term.key, keys)
    })
  }

  const _setPlotType = () => {
    if (!_controller) return
    const viewType = _controller.Chart.get_Chart().get_View()
    if (
      (viewType instanceof window.Trading.Chart.TSView &&
        plotType.id === PLOTTYPE[3].id) ||
      (!(viewType instanceof window.Trading.Chart.TSView) &&
        plotType.id !== PLOTTYPE[3].id)
    ) {
      // Viewを切り替える場合、reDrawする必要があるため以下4つの関数が必要
      setViewType(plotType.key, _controller)
      setPlotType(plotType.key, _controller)
      setSymbolAndLegs(term.key, symbol, _provider, _controller)
      _controller.Chart.Draw()
      // Addon / SubChartを登録し直す必要がある。
      if (
        !(viewType instanceof window.Trading.Chart.TSView) &&
        plotType.id !== PLOTTYPE[3].id
      ) {
        if (!_.isEmpty(subSymbolNode))
          _.forEach(subSymbolNode, items => removeSubSymbol(items, _controller))
        if (!relativeChartFlag) {
          _setSubChart()
          _setAddonChart()
        }
      }

      dispatch(figureAllClear(canvasId))
    } else {
      setPlotType(plotType.key, _controller)
      dispatch(figureAllClear(canvasId))
    }
  }

  useEffect(() => {
    if (!allProperties || _.isEmpty(allProperties)) return
    const _data = _.pick(allProperties, [
      "addonChartOptions",
      "cycleAnaTypes",
      "plotType",
      "priceVolumeParam",
      "subChartOptions",
      "symbol",
      "subSymbol",
      "tenkanKikan",
      "term",
      "addonChart",
      "addonSelected",
      "subChart",
      "subSelected",
    ])
    const _tools = _.clone(tools)
    _.pullAt(_tools, _.findIndex(tools, _.pick(TOOLS[4], pickTarget)))
    _.set(_data, "tools", _tools)
    UserStorage.TechChart.set(_data)
  }, [allProperties])

  useEffect(() => {
    if (window.Logic === undefined) return
    dispatch(pageLoadRegist(canvasId, true))
    createCProvider()
    dispatch(pageLoadRegist(canvasId, false))
  }, [])

  useEffect(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    applyOption()
    resize()
    dispatch(pageLoadRegist(canvasId, false))
    return () => removeResize()
  }, [_controller])

  // term, symbol, subSymbolで同じ関数だが、初回レンダリング時の処理スキップのため別々に定義。
  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    setSymbolAndLegs(term.key, symbol, _provider, _controller)
    // Addon / SubChartを登録し直す必要がある。
    // if (plotType.id !== PLOTTYPE[3].id) {
    //   if (!_.isEmpty(subSymbolNode))
    //     _.forEach(subSymbolNode, items => removeSubSymbol(items, _controller))
    //   _setSubSymbol()
    // }
    dispatch(figureAllClear(canvasId))
    resetUiSpan(canvasId, _controller)
    dispatch(pageLoadRegist(canvasId, false))
  }, [term])

  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    setSymbolAndLegs(term.key, symbol, _provider, _controller)
    // Addon / SubChartを登録し直す必要がある。
    // if (plotType.id !== PLOTTYPE[3].id) {
    //   if (!_.isEmpty(subSymbolNode))
    //     _.forEach(subSymbolNode, items => removeSubSymbol(items, _controller))
    //   _setSubSymbol()
    // }
    dispatch(figureAllClear(canvasId))
    resetUiSpan(canvasId, _controller)
    dispatch(pageLoadRegist(canvasId, false))
  }, [symbol])

  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    // setSymbolAndLegs(term.key, symbol, _provider, _controller)
    if (plotType.id !== PLOTTYPE[3].id) {
      if (!_.isEmpty(subSymbolNode))
        _.forEach(subSymbolNode, items => removeSubSymbol(items, _controller))
      _setSubSymbol()
    }
    dispatch(figureAllClear(canvasId))
    //     resetUiSpan(canvasId, _controller)
    dispatch(pageLoadRegist(canvasId, false))
  }, [subSymbol])

  useEffectFirstSkip(() => {
    if (plotType.id !== PLOTTYPE[3].id) {
      if (!relativeChartFlag) {
        _setSubChart()
        _setAddonChart()
      }
    }
  }, [symbol])

  // term切り替え時に、addon/subを入れ替える処理。
  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    setPrevAddonSelected([])
    setPrevSubSelected([])
    const _subChart = _.clone(subChart[term.id])
    _.remove(_subChart, (items: any) => {
      return items.id === SUB_CHART[19].id
    })
    dispatch(
      changeAddonSelected(
        canvasId,
        _.map(addonChart[term.id], items => {
          return items.id
        })
      )
    )
    dispatch(
      changeSubSelected(
        canvasId,
        _.map(_subChart, items => {
          return items.id
        })
      )
    )
    // if (!relativeChartFlag) {
    //   _setAddonChart()
    //   _setSubChart()
    // }
    dispatch(pageLoadRegist(canvasId, false))
  }, [term])

  useEffectFirstSkip(() => {
    dispatch(pageLoadRegist(canvasId, true))
    _setPlotType()
    dispatch(pageLoadRegist(canvasId, false))
  }, [plotType])

  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    setFigure(
      figureType.key,
      figureOptions,
      "middle",
      colorSetting,
      _controller
    )
    setFigureCommand(
      figureCommand.key,
      _controller,
      figureCommand.key === FIGURE_COMMAND[3].key
        ? () => dispatch(changeFigureCommand(canvasId, null))
        : null
    )
    dispatch(pageLoadRegist(canvasId, false))
  }, [figureOptions, figureCommand])

  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    colorSettings()
    dispatch(pageLoadRegist(canvasId, false))
  }, [compChartColor, subSymbolNode])

  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    const elements = document.querySelector("html").className.split(" ")
    if (_.includes(elements, "dark")) {
      UserStorage.TechChartColor.setDark(colorSetting)
    } else {
      UserStorage.TechChartColor.setWhite(colorSetting)
    }
    setCompChartColor(_setCompChartColor())
    chartColors.setColorPattern(plotType.key, colorSetting, _controller)
    dispatch(pageLoadRegist(canvasId, false))
  }, [colorSetting])

  useEffectFirstSkip(() => {
    UserStorage.ChartSetting.set(chartSetting)
  }, [chartSetting])

  const cheakFalse = (target: number) => {
    return !target && target !== 0
  }

  // sso元のparamを監視。
  useEffect(() => {
    const securityType = Number(_.get(param, "SecurityType"))
    const exchange = Number(_.get(param, "Exchange"))
    const symbol = _.get(param, "Symbol")
    if (cheakFalse(securityType) || cheakFalse(exchange) || !symbol) return
    ;(async () => {
      dispatch(pageLoadRegist(canvasId, true))
      const res = await APIs.SymbolDetail.SymbolDetail({
        SecurityType: securityType,
        Exchange: exchange,
        Symbol: symbol,
      })
      const data = _.get(res, "Symbol")
      if (_.isEmpty(data)) return
      dispatch(changeSymbol(canvasId, data))
      dispatch(pageLoadRegist(canvasId, false))
    })()
    const _plotType = Number(_.get(param, "plotType", 0))
    const _term = Number(_.get(param, "term", 8))
    dispatch(changePlotType(canvasId, _plotType))
    dispatch(changeTerm(canvasId, _term))
  }, [param])

  // ADDON SUBの処理。比較チャートの有無で挙動が変わる。
  // スプレッド（サブ）のみは、比較チャート中に使える。
  useEffectFirstSkip(() => {
    if (!_controller || _.isEqual(prevAddonSelected, addonSelected)) return
    dispatch(pageLoadRegist(canvasId, true))
    const removeTarget: Array<number> = _.difference(
      prevAddonSelected,
      addonSelected
    )
    const addTarget: Array<number> = _.difference(
      addonSelected,
      prevAddonSelected
    )
    setPrevAddonSelected(addonSelected)
    if (relativeChartFlag) return
    _.forEach(addTarget, id => {
      setAddonChart(chartSetting, ADDON_CHART[id].key, _controller)
    })
    _.forEach(removeTarget, id => {
      removeAddonChart(ADDON_CHART[id].key, _controller)
    })
    dispatch(changeAddonChart(canvasId, addonSelected, term.id))
    dispatch(pageLoadRegist(canvasId, false))
  }, [addonSelected])

  useEffect(() => {
    if (!_controller || _.isEqual(prevSubSelected, subSelected)) return
    dispatch(pageLoadRegist(canvasId, true))
    const removeTarget: Array<number> = _.difference(
      prevSubSelected,
      subSelected
    )
    const addTarget: Array<number> = _.difference(subSelected, prevSubSelected)
    setPrevSubSelected(subSelected)
    if (relativeChartFlag) return
    _.forEach(addTarget, id => {
      setSubChart(chartSetting, SUB_CHART[id].key, _controller)
    })
    _.forEach(removeTarget, id => {
      removeSubChart(SUB_CHART[id].key, _controller)
    })
    dispatch(changeSubChart(canvasId, subSelected, term.id))
    dispatch(pageLoadRegist(canvasId, false))
  }, [subSelected])

  useEffectFirstSkip(() => {
    if (!_controller || relativeChartFlag) return
    dispatch(pageLoadRegist(canvasId, true))
    _.forEach(addonChart[term.id], items => {
      let keys: Array<string> = []
      _.forEach(addonChartOptions[term.id][items.key], options => {
        keys.push(options.key)
      })
      setAddonChart(chartSetting, items.key, _controller, term.key, keys, true)
    })
    dispatch(pageLoadRegist(canvasId, false))
  }, [addonChart[term.id]])

  // スプレッドのみ別管理している。
  useEffectFirstSkip(() => {
    dispatch(pageLoadRegist(canvasId, true))
    if (!_controller) return
    if (spred && relativeChartFlag) {
      setSubChart(chartSetting, SUB_CHART[19].key, _controller)
      const _subSelected = _.clone(subSelected)
      _subSelected.push(SUB_CHART[19].id)
      dispatch(changeSubChart(canvasId, _subSelected, term.id))
    }
    if (!spred) {
      removeSubChart(SUB_CHART[19].key, _controller)
      const _subSelected = _.clone(subSelected)
      _.pull(_subSelected, SUB_CHART[19].id)
      dispatch(changeSubChart(canvasId, _subSelected, term.id))
    }
    dispatch(pageLoadRegist(canvasId, true))
  }, [spred])

  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    _.forEach(subChart[term.id], items => {
      if (relativeChartFlag) return
      let keys: Array<string> = []
      _.forEach(subChartOptions[term.id][items.key], options => {
        keys.push(options.key)
      })
      setSubChart(chartSetting, items.key, _controller, term.key, keys, true)
    })
    dispatch(pageLoadRegist(canvasId, false))
  }, [subChart[term.id]])

  // 比較チャートではなくなった際に、addon/subを追加する処理。
  useEffectFirstSkip(() => {
    if (!_controller) return
    dispatch(pageLoadRegist(canvasId, true))
    if (!relativeChartFlag) {
      _.forEach(addonSelected, id => {
        setAddonChart(chartSetting, ADDON_CHART[id].key, _controller)
      })
      _.forEach(subSelected, id => {
        setSubChart(chartSetting, SUB_CHART[id].key, _controller)
      })
      dispatch(changeAddonChart(canvasId, addonSelected, term.id))
      dispatch(changeSubChart(canvasId, subSelected, term.id))
      // ↓　スプレッドを外す処理。
      dispatch(changeSubSelected(canvasId, subSelected))
    }
    dispatch(pageLoadRegist(canvasId, false))
    if (relativeChartFlag) {
      _.forEach(addonSelected, id => {
        removeAddonChart(ADDON_CHART[id].key, _controller)
      })
      _.forEach(subSelected, id => {
        removeSubChart(SUB_CHART[id].key, _controller)
      })
    }
  }, [relativeChartFlag])

  return (
    <div
      id={`chart-body-${canvasId}`}
      style={{ width: "100%", height: "100%" }}
    >
      <canvas
        id={canvasId}
        width={scaleWidth}
        height={scaleHeight}
        style={{
          cursor: cursor,
          width: `${width}px`,
          height: `${height}px`,
        }}
      />
    </div>
  )
})

export default TechChart
